import { RefreshCw } from 'lucide-react';
import type { FSFilters, FSFilterOptions, FSView } from '../../types/financialStatement';

interface Props {
    filters: FSFilters;
    filterOptions: FSFilterOptions;
    onFilterChange: (updates: Partial<FSFilters>) => void;
    onRefresh: () => void;
    loading: boolean;
    hideZeros: boolean;
    onHideZerosChange: (value: boolean) => void;
    incomeViews: FSView[];
    balanceViews: FSView[];
    selectedIncomeViewId: number | null;
    selectedBalanceViewId: number | null;
    onIncomeViewChange: (viewId: number) => void;
    onBalanceViewChange: (viewId: number) => void;
}

export function StatementFilters({
    filters, filterOptions, onFilterChange, onRefresh, loading,
    hideZeros, onHideZerosChange,
    incomeViews, balanceViews,
    selectedIncomeViewId, selectedBalanceViewId,
    onIncomeViewChange, onBalanceViewChange,
}: Props) {
    return (
        <div className="flex flex-wrap items-center gap-3">
            {/* Company */}
            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
                <select
                    value={filters.company}
                    onChange={e => onFilterChange({ company: e.target.value })}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                    {filterOptions.companies.map(c => (
                        <option key={c.code} value={c.code}>{c.code.toUpperCase()} - {c.name}</option>
                    ))}
                </select>
            </div>

            {/* Year */}
            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Year</label>
                <select
                    value={filters.year}
                    onChange={e => onFilterChange({
                        year: Number(e.target.value),
                        comparisonYear: Number(e.target.value) - 1,
                    })}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                    {filterOptions.years.map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            </div>

            {/* Comparison Year */}
            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Comparison Year</label>
                <select
                    value={filters.comparisonYear}
                    onChange={e => onFilterChange({ comparisonYear: Number(e.target.value) })}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                    {filterOptions.years.map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            </div>

            {/* Income View */}
            {incomeViews.length > 0 && (
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Income View</label>
                    <select
                        value={selectedIncomeViewId ?? ''}
                        onChange={e => onIncomeViewChange(Number(e.target.value))}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                        {incomeViews.map(v => (
                            <option key={v.ViewId} value={v.ViewId}>{v.ViewName}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Balance View */}
            {balanceViews.length > 0 && (
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Balance View</label>
                    <select
                        value={selectedBalanceViewId ?? ''}
                        onChange={e => onBalanceViewChange(Number(e.target.value))}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                        {balanceViews.map(v => (
                            <option key={v.ViewId} value={v.ViewId}>{v.ViewName}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Hide Zero Values */}
            <div className="flex items-end">
                <label className="inline-flex items-center gap-2 cursor-pointer py-1.5">
                    <input
                        type="checkbox"
                        checked={hideZeros}
                        onChange={e => onHideZerosChange(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                    />
                    <span className="text-sm text-gray-600">Hide Zero Values</span>
                </label>
            </div>

            {/* Reload */}
            <div className="ml-auto">
                <label className="block text-xs font-medium text-transparent mb-1">.</label>
                <button
                    onClick={onRefresh}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Reload
                </button>
            </div>
        </div>
    );
}
